import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
} from "@mui/material";
import {
  ArrowBackIosNew,
  Send,
  Done,
  DoneAll,
  KeyboardArrowDown,
  EmojiEmotions,
  Image as ImageIcon,
  AttachFile,
  GroupAdd,
  DeleteOutline,
} from "@mui/icons-material";
import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  where,
  serverTimestamp,
  setDoc,
  addDoc,
  arrayUnion,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { auth, db } from "./firebase";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const NAV_OFFSET = 64;

const canUseNotifications = () => typeof window !== "undefined" && "Notification" in window;

const notifyMessage = ({ title, body }) => {
  if (!canUseNotifications() || Notification.permission !== "granted") return;
  const text = String(body || "").trim();
  if (!text) return;
  new Notification(title || "Gexvi", {
    body: text,
    icon: "/logo192.png",
    tag: `chat-${Date.now()}`,
  });
};

const MESSAGES_TEXT = {
  es: {
    messages: "Mensajes",
    noConversations: "No tienes conversaciones aún. Envía un mensaje desde el perfil de un usuario.",
    members: "integrantes",
    noMessages: "Sin mensajes",
    group: "Grupo",
    newGroup: "Nuevo grupo",
    groupName: "Nombre del grupo",
    selectMembers: "Selecciona miembros",
    cancel: "Cancelar",
    createGroup: "Crear grupo",
    typeMessage: "Escribe un mensaje...",
    loadError: "No se pudieron cargar los mensajes",
    userFallback: "Usuario",
    deleteChatTitle: "Eliminar chat",
    deleteChatWarning: "Esta conversación se borrará de tu lista y se eliminarán sus mensajes.",
    delete: "Eliminar",
    newMessageFallback: "Nuevo mensaje",
    gotMessageFallback: "Te enviaron un mensaje",
    newActivityFallback: "Tienes actividad nueva",
  },
  en: {
    messages: "Messages",
    noConversations: "You do not have conversations yet. Send a message from a user's profile.",
    members: "members",
    noMessages: "No messages",
    group: "Group",
    newGroup: "New group",
    groupName: "Group name",
    selectMembers: "Select members",
    cancel: "Cancel",
    createGroup: "Create group",
    typeMessage: "Type a message...",
    loadError: "Could not load messages",
    userFallback: "User",
    deleteChatTitle: "Delete chat",
    deleteChatWarning: "This conversation will be removed from your list and its messages will be deleted.",
    delete: "Delete",
    newMessageFallback: "New message",
    gotMessageFallback: "You got a new message",
    newActivityFallback: "You have new activity",
  },
};

// ─── Pantalla de conversaciones ────────────────────────────────────────────
const ConversacionesList = ({ onOpenChat, onBack, onEnableNotifications, notificationEnabled, t, themeMode }) => {
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const currentUser = auth.currentUser;
  const [chats, setChats] = useState([]);
  const [users, setUsers] = useState([]);
  const [postProfilesByUid, setPostProfilesByUid] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [openCreateGroup, setOpenCreateGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [deletingChat, setDeletingChat] = useState(false);
  const isInitialSnapshotRef = useRef(true);
  const previousChatsRef = useRef(new Map());

  const resolveIdentity = useCallback((uid, fallbackName = "") => {
    const userDoc = users.find((u) => u.uid === uid) || {};
    const postProfile = postProfilesByUid[uid] || {};
    const name =
      userDoc.displayName ||
      userDoc.username ||
      postProfile.displayName ||
      userDoc.handle ||
      postProfile.handle ||
      fallbackName ||
      t("userFallback");
    const photo =
      userDoc.photoURL ||
      userDoc.photoUrl ||
      userDoc.userImg ||
      userDoc.avatar ||
      postProfile.photoURL ||
      "";
    return { name, photo };
  }, [users, postProfilesByUid, t]);

  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, "chats"),
      where("participants", "array-contains", currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const aMillis = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bMillis = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bMillis - aMillis;
        });

      if (isInitialSnapshotRef.current) {
        isInitialSnapshotRef.current = false;
      } else {
        items.forEach((chat) => {
          const prev = previousChatsRef.current.get(chat.id);
          const wasUpdated = prev && prev.lastMessage !== chat.lastMessage;
          const fromOtherUser = chat.lastMessageSenderUid && chat.lastMessageSenderUid !== currentUser.uid;
          const appInBackground = document.visibilityState === "hidden" || !document.hasFocus();
          if (wasUpdated && fromOtherUser && appInBackground) {
            const otherUid = chat.participants?.find((p) => p !== currentUser.uid);
            const otherIdentity = resolveIdentity(otherUid, chat.participantNames?.[otherUid] || "");
            const chatTitle = chat.isGroup
              ? chat.groupName || t("newMessageFallback")
              : otherIdentity.name || t("newMessageFallback");
            notifyMessage({ title: chatTitle, body: chat.lastMessage || t("gotMessageFallback") });
          }
        });
      }

      previousChatsRef.current = new Map(items.map((chat) => [chat.id, chat]));
      setChats(items);
      setLoadError("");
      setLoading(false);
    }, (error) => {
      console.error("Error cargando chats", error);
      setLoadError(t("loadError"));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentUser, resolveIdentity, t]);

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(collection(db, "users"), (snap) => {
      const items = snap.docs
        .map((d) => ({ uid: d.id, ...d.data() }))
        .filter((u) => u.uid !== currentUser.uid);
      setUsers(items);
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "posts"), (snap) => {
      const profileMap = {};
      snap.docs.forEach((d) => {
        const post = d.data() || {};
        if (!post.uid) return;
        const current = profileMap[post.uid] || {};
        profileMap[post.uid] = {
          displayName: current.displayName || post.username || "",
          handle: current.handle || post.handle || "",
          photoURL: current.photoURL || post.userImg || post.photoURL || post.photoUrl || "",
        };
      });
      setPostProfilesByUid(profileMap);
    });
    return () => unsubscribe();
  }, []);

  const toggleMember = (uid) => {
    setSelectedMemberIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  const handleCreateGroup = async () => {
    if (!currentUser || creatingGroup) return;
    const name = groupName.trim();
    if (!name || selectedMemberIds.length === 0) return;

    setCreatingGroup(true);
    try {
      const selectedUsers = users.filter((u) => selectedMemberIds.includes(u.uid));
      const participantIds = [currentUser.uid, ...selectedMemberIds];
      const participantNames = {
        [currentUser.uid]:
          currentUser.displayName || currentUser.email?.split("@")[0] || "Yo",
      };
      const participantPhotos = {
        [currentUser.uid]: currentUser.photoURL || "",
      };

      selectedUsers.forEach((u) => {
        const identity = resolveIdentity(u.uid);
        participantNames[u.uid] = identity.name;
        participantPhotos[u.uid] = identity.photo;
      });

      const chatRef = await addDoc(collection(db, "chats"), {
        isGroup: true,
        groupName: name,
        participants: participantIds,
        members: participantIds,
        participantNames,
        participantPhotos,
        lastMessage: "",
        lastMessageSenderUid: currentUser.uid,
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setOpenCreateGroup(false);
      setGroupName("");
      setSelectedMemberIds([]);

      onOpenChat({
        chatId: chatRef.id,
        isGroup: true,
        groupName: name,
        displayName: name,
        participants: participantIds,
        participantNames,
        participantPhotos,
      });
    } catch (error) {
      console.error("No se pudo crear el grupo", error);
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleDeleteChat = async () => {
    if (!chatToDelete || deletingChat) return;
    setDeletingChat(true);
    try {
      const messagesSnap = await getDocs(collection(db, "chats", chatToDelete.id, "messages"));
      await Promise.all(messagesSnap.docs.map((messageDoc) => deleteDoc(messageDoc.ref)));
      await deleteDoc(doc(db, "chats", chatToDelete.id));
      if (currentUser?.uid && previousChatsRef.current.has(chatToDelete.id)) {
        previousChatsRef.current.delete(chatToDelete.id);
      }
      setChatToDelete(null);
    } catch (error) {
      console.error("No se pudo eliminar el chat", error);
    } finally {
      setDeletingChat(false);
    }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 200, color: onSurface(1), background: isLightTheme ? "linear-gradient(to bottom, #ffffff, #f2f5f8)" : "linear-gradient(to bottom, #101010, #000)", display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", width: "100%", maxWidth: "100vw", boxSizing: "border-box" }}>
      <Box
        sx={{
          position: "sticky", top: 0,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.9)" : "rgba(10,10,10,0.88)",
          backdropFilter: "blur(12px)",
          zIndex: 10,
          borderBottom: `1px solid ${onSurface(0.07)}`,
        }}
      >
        <Box sx={{ maxWidth: 680, mx: "auto", width: "100%", px: 1.5, py: 1.5, display: "flex", alignItems: "center", gap: 1, boxSizing: "border-box" }}>
          <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
            <ArrowBackIosNew />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>{t("messages")}</Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton
            onClick={() => setOpenCreateGroup(true)}
            sx={{ color: onSurface(0.7) }}
          >
            <GroupAdd fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 680, mx: "auto", width: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress sx={{ color: "#00d4ff" }} />
          </Box>
        ) : loadError ? (
          <Typography sx={{ color: "#ff9a9a", textAlign: "center", mt: 6, px: 2 }}>
            {loadError}
          </Typography>
        ) : chats.length === 0 ? (
          <Typography sx={{ color: onSurface(0.5), textAlign: "center", mt: 6, px: 2 }}>
            {t("noConversations")}
          </Typography>
        ) : (
          <List sx={{ px: 1 }}>
            {chats.map((chat) => {
              const isGroup = Boolean(chat.isGroup);
              const otherUid = chat.participants?.find((p) => p !== currentUser.uid);
              const otherIdentity = resolveIdentity(otherUid, chat.participantNames?.[otherUid] || "");
              const otherName = otherIdentity.name;
              const otherPhoto = chat.participantPhotos?.[otherUid] || otherIdentity.photo || "";
              const groupTitle = chat.groupName || t("group");
              const chatTitle = isGroup ? groupTitle : otherName;
              const chatPhoto = isGroup ? (chat.groupPhotoURL || "") : otherPhoto;
              return (
                <React.Fragment key={chat.id}>
                  <ListItem
                    onClick={() =>
                      onOpenChat({
                        chatId: chat.id,
                        uid: otherUid,
                        displayName: chatTitle,
                        photoURL: chatPhoto,
                        isGroup,
                        groupName: groupTitle,
                        participants: chat.participants || [],
                        participantNames: chat.participantNames || {},
                        participantPhotos: chat.participantPhotos || {},
                      })
                    }
                    sx={{ borderRadius: "14px", cursor: "pointer", "&:hover": { bgcolor: onSurface(0.06) } }}
                  >
                    <ListItemAvatar>
                      <Avatar src={chatPhoto}>{isGroup ? "G" : undefined}</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={<Typography variant="subtitle2" sx={{ color: onSurface(1), fontWeight: 600 }}>{chatTitle}</Typography>}
                      secondary={
                        <Typography variant="caption" sx={{ color: onSurface(0.5), display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {isGroup
                            ? `${chat.participants?.length || 0} ${t("members")}${chat.lastMessage ? ` · ${chat.lastMessage}` : ""}`
                            : (chat.lastMessage || t("noMessages"))}
                        </Typography>
                      }
                    />
                    <IconButton
                      onClick={(event) => {
                        event.stopPropagation();
                        setChatToDelete({ id: chat.id, title: chatTitle });
                      }}
                      sx={{ color: onSurface(0.45), ml: 0.5, '&:hover': { color: '#ff6b6b', bgcolor: 'rgba(255,107,107,0.08)' } }}
                      size="small"
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </ListItem>
                  <Divider sx={{ bgcolor: onSurface(0.06) }} />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Box>

      <Dialog
        open={openCreateGroup}
        onClose={() => !creatingGroup && setOpenCreateGroup(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: isLightTheme ? "#ffffff" : "#0b1016",
            color: onSurface(1),
            borderRadius: "16px",
            backgroundImage: isLightTheme
              ? "radial-gradient(circle at top, rgba(0,150,180,0.06) 0%, rgba(255,255,255,1) 55%)"
              : "radial-gradient(circle at top, rgba(0,212,255,0.12) 0%, rgba(11,16,22,1) 52%)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("newGroup")}</DialogTitle>
        <DialogContent>
          <TextField
            label={t("groupName")}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            sx={{
              mt: 1,
              "& .MuiInputBase-root": { color: onSurface(1), bgcolor: onSurface(0.04), borderRadius: "10px" },
              "& .MuiInputLabel-root": { color: onSurface(0.62) },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: onSurface(0.16) },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: onSurface(0.3) },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#00d4ff" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#00d4ff" },
            }}
          />

          <Typography variant="caption" sx={{ color: onSurface(0.6), display: "block", mt: 2, mb: 1 }}>
            {t("selectMembers")}
          </Typography>

          <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
            {users.map((u) => {
              const selected = selectedMemberIds.includes(u.uid);
              const identity = resolveIdentity(u.uid);
              return (
                <Box
                  key={u.uid}
                  onClick={() => toggleMember(u.uid)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1,
                    borderRadius: "10px",
                    cursor: "pointer",
                    bgcolor: selected ? "rgba(0,212,255,0.12)" : "transparent",
                    "&:hover": { bgcolor: selected ? "rgba(0,212,255,0.16)" : onSurface(0.05) },
                  }}
                >
                  <Checkbox checked={selected} sx={{ color: onSurface(0.5), "&.Mui-checked": { color: "#00d4ff" } }} />
                  <Avatar src={identity.photo} sx={{ width: 36, height: 36 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: onSurface(1) }}>
                      {identity.name}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: onSurface(0.55), display: "block" }}>
                      @{u.username || u.handle || postProfilesByUid[u.uid]?.handle || "usuario"}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setOpenCreateGroup(false)}
            disabled={creatingGroup}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleCreateGroup}
            disabled={creatingGroup || !groupName.trim() || selectedMemberIds.length === 0}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 130,
              background: "linear-gradient(90deg, #00d4ff 0%, #1498ff 100%)",
            }}
          >
            {creatingGroup ? <CircularProgress size={20} sx={{ color: "#00131a" }} /> : t("createGroup")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(chatToDelete)}
        onClose={() => !deletingChat && setChatToDelete(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: isLightTheme ? "#ffffff" : "#0b1016",
            color: onSurface(1),
            borderRadius: "16px",
            border: `1px solid ${onSurface(0.08)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("deleteChatTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: onSurface(0.72) }}>
            {t("deleteChatWarning")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setChatToDelete(null)}
            disabled={deletingChat}
            sx={{ color: onSurface(0.75), textTransform: "none" }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDeleteChat}
            disabled={deletingChat}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              minWidth: 120,
              bgcolor: "#ff4d4d",
              "&:hover": { bgcolor: "#ff6b6b" },
            }}
          >
            {deletingChat ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Pantalla de chat individual ────────────────────────────────────────────
const ChatIndividual = ({ chatInfo, onBack, onEnableNotifications, notificationEnabled, t, themeMode }) => {
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const currentUser = auth.currentUser;
  const {
    chatId,
    uid: otherUid,
    displayName,
    photoURL,
    isGroup,
    groupName,
    participants,
    participantNames,
    participantPhotos,
  } = chatInfo;
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  const hasLoadedMessagesRef = useRef(false);
  const lastMessageIdRef = useRef(null);
  // Guardamos los IDs de mensajes ya marcados como leídos para no repetir writes
  const markedAsReadRef = useRef(new Set());

  const EMOJIS = ["😀","😂","🥰","😎","🤔","😢","😡","🎉","🔥","❤️","👍","👏","🙌","💯","✨","😅","🤣","😍","🥺","😤","😴","🤯","👀","🫶","💪","🙏","😊","🫠","💀","🤝"];

  const resolvedChatId = chatId || (otherUid ? [currentUser?.uid, otherUid].sort().join("_") : "");
  const chatTitle = isGroup ? (groupName || displayName || t("group")) : (displayName || t("userFallback"));

  const toMillis = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === "function") return value.toMillis();
    const dateValue = typeof value.toDate === "function" ? value.toDate() : new Date(value);
    const ms = dateValue?.getTime?.() || 0;
    return Number.isFinite(ms) ? ms : 0;
  };

  // ─── Suscripción a mensajes ───────────────────────────────────────────────
  useEffect(() => {
    if (!resolvedChatId) return;
    const q = query(
      collection(db, "chats", resolvedChatId, "messages"),
      orderBy("timestamp", "asc")
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [resolvedChatId]);

  // ─── Marcar como leídos (estilo WhatsApp) ────────────────────────────────
  // Cada vez que llegan mensajes, buscamos los mensajes del otro usuario
  // que aún no tienen nuestro UID en readBy, y los marcamos en batch.
  useEffect(() => {
    if (!messages.length || !currentUser || !resolvedChatId) return;

    // Mensajes que NO son míos y que aún no tienen mi UID en readBy
    const unread = messages.filter(
      (msg) =>
        msg.uid &&
        msg.uid !== currentUser.uid &&
        !Array.isArray(msg.readBy || []) === false &&  // siempre array
        !(msg.readBy || []).includes(currentUser.uid) &&
        !markedAsReadRef.current.has(msg.id)           // evitar re-escrituras
    );

    if (unread.length === 0) return;

    // Marcamos localmente de inmediato para no re-procesar
    unread.forEach((msg) => markedAsReadRef.current.add(msg.id));

    // Batch write: añadir currentUser.uid al readBy de cada mensaje no leído
    const batch = writeBatch(db);
    unread.forEach((msg) => {
      const msgRef = doc(db, "chats", resolvedChatId, "messages", msg.id);
      batch.update(msgRef, {
        readBy: arrayUnion(currentUser.uid),
      });
    });
    batch.commit().catch((err) => {
      // Si falla, quitamos del set para que reintente en el próximo render
      unread.forEach((msg) => markedAsReadRef.current.delete(msg.id));
      console.error("Error marcando mensajes como leídos:", err);
    });
  }, [messages, currentUser, resolvedChatId]);

  // ─── Scroll al fondo cuando llegan mensajes ───────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ─── Notificaciones en background ────────────────────────────────────────
  useEffect(() => {
    if (!messages.length) return;
    const latest = messages[messages.length - 1];

    if (!hasLoadedMessagesRef.current) {
      hasLoadedMessagesRef.current = true;
      lastMessageIdRef.current = latest.id;
      return;
    }

    const isNewMessage = latest.id !== lastMessageIdRef.current;
    const fromOtherUser = latest.uid && latest.uid !== currentUser?.uid;
    const appInBackground = document.visibilityState === "hidden" || !document.hasFocus();

    if (isNewMessage && fromOtherUser && appInBackground) {
      notifyMessage({ title: chatTitle || t("newMessageFallback"), body: latest.text || t("gotMessageFallback") });
    }

    lastMessageIdRef.current = latest.id;
  }, [messages, currentUser?.uid, chatTitle, t]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(distanceFromBottom > 200);
  };

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    const trimmed = text.trim();
    if (!trimmed || !currentUser || sending) return;
    if (!resolvedChatId) {
      console.error("No se puede enviar: destinatario invalido");
      return;
    }

    const participantIds =
      Array.isArray(participants) && participants.length > 0
        ? participants
        : [currentUser.uid, otherUid].filter(Boolean);

    setSending(true);
    try {
      const currentUserProfile = {
        displayName: currentUser.displayName || currentUser.email?.split("@")[0] || "Yo",
        photoURL: currentUser.photoURL || "",
      };
      const payload = {
        participants: participantIds,
        members: participantIds,
        lastMessage: trimmed,
        lastMessageSenderUid: currentUser.uid,
        updatedAt: serverTimestamp(),
      };

      if (isGroup) {
        payload.isGroup = true;
        payload.groupName = chatTitle;
        payload.participantNames = {
          ...(participantNames || {}),
          [currentUser.uid]: (participantNames && participantNames[currentUser.uid]) || currentUserProfile.displayName,
        };
        payload.participantPhotos = {
          ...(participantPhotos || {}),
          [currentUser.uid]: (participantPhotos && participantPhotos[currentUser.uid]) || currentUserProfile.photoURL,
        };
      } else {
        payload.participantNames = {
          [currentUser.uid]: currentUserProfile.displayName,
          [otherUid]: displayName || t("userFallback"),
        };
        payload.participantPhotos = {
          [currentUser.uid]: currentUserProfile.photoURL,
          [otherUid]: photoURL || "",
        };
      }

      await setDoc(
        doc(db, "chats", resolvedChatId),
        payload,
        { merge: true }
      );

      // Mensaje nuevo: readBy arranca vacío, el remitente NO se pone a sí mismo
      await addDoc(collection(db, "chats", resolvedChatId, "messages"), {
        uid: currentUser.uid,
        text: trimmed,
        timestamp: serverTimestamp(),
        readBy: [],   // el receptor lo llenará cuando abra el chat
      });

      setText("");
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Helpers para los ticks de estado ────────────────────────────────────
  // Para chat 1-a-1: el "otro" participante es otherUid
  // Para grupos: todos los participantes menos yo deben haber leído
  const getReadStatus = (msg) => {
    const isMe = msg.uid === currentUser?.uid;
    if (!isMe) return null; // solo mostramos ticks en los mensajes propios

    const readBy = msg.readBy || [];
    const isPersisted = Boolean(toMillis(msg.timestamp));

    if (!isGroup) {
      // 1-a-1: visto = otherUid está en readBy
      const seen = otherUid && readBy.includes(otherUid);
      if (seen) return "seen";
      return isPersisted ? "delivered" : "sent";
    } else {
      // Grupo: visto = todos los demás miembros han leído
      const others = (participants || []).filter((uid) => uid !== currentUser?.uid);
      if (others.length === 0) return isPersisted ? "delivered" : "sent";
      const allSeen = others.every((uid) => readBy.includes(uid));
      if (allSeen) return "seen";
      return isPersisted ? "delivered" : "sent";
    }
  };

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", flexDirection: "column", background: isLightTheme ? "linear-gradient(to bottom, #ffffff, #f2f5f8)" : "linear-gradient(to bottom, #101010, #000)", color: onSurface(1), overflow: "hidden", width: "100%", maxWidth: "100vw", boxSizing: "border-box" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: isLightTheme ? "rgba(255,255,255,0.9)" : "rgba(10,10,10,0.88)",
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${onSurface(0.07)}`,
          zIndex: 10, flexShrink: 0,
        }}
      >
        <Box sx={{ maxWidth: 680, mx: "auto", width: "100%", px: 1.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.5, boxSizing: "border-box" }}>
          <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
            <ArrowBackIosNew />
          </IconButton>
          <Avatar src={isGroup ? "" : photoURL} sx={{ width: 38, height: 38, border: "2px solid rgba(0,212,255,0.4)" }}>
            {isGroup ? (chatTitle?.charAt(0) || "G").toUpperCase() : null}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{chatTitle}</Typography>
            {isGroup && (
              <Typography variant="caption" sx={{ color: onSurface(0.55) }}>
                {`${participants?.length || 0} ${t("members")}`}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
        </Box>
      </Box>

      {/* Área de mensajes */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", px: 2, py: 1, pb: "88px", boxSizing: "border-box" }}
      >
        <Box sx={{ maxWidth: 680, mx: "auto", width: "100%", boxSizing: "border-box" }}>
          {messages.map((msg) => {
            const isMe = Boolean(currentUser?.uid && msg.uid === currentUser.uid);
            const readStatus = getReadStatus(msg); // null | "sent" | "delivered" | "seen"
            const msgTimeMs = toMillis(msg.timestamp);

            return (
              <Box
                key={msg.id}
                sx={{ display: "flex", justifyContent: isMe ? "flex-end" : "flex-start", mb: 1, alignItems: "flex-end", gap: 0.5 }}
              >
                <Box
                  sx={{
                    maxWidth: "72%",
                    bgcolor: isMe ? "#00d4ff" : onSurface(isLightTheme ? 0.08 : 0.1),
                    color: isMe ? "#000" : onSurface(1),
                    px: 2, py: 1,
                    borderRadius: isMe ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                  }}
                >
                  <Typography variant="body2">{msg.text}</Typography>

                  {/* Footer del mensaje: hora + ticks */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, mt: 0.45, lineHeight: 1 }}>
                    <Typography variant="caption" sx={{ fontSize: "11px", color: isMe ? "rgba(0,0,0,0.5)" : onSurface(0.4) }}>
                      {msgTimeMs ? new Date(msgTimeMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                    </Typography>

                    {/* Ticks: solo en mis mensajes */}
                    {readStatus === "seen" && (
                      // ✓✓ azul = VISTO
                      <DoneAll
                        sx={{
                          fontSize: 16,
                          color: "#1d4ed8",
                          ml: 0.25,
                        }}
                      />
                    )}
                    {readStatus === "delivered" && (
                      // ✓✓ gris = ENTREGADO
                      <DoneAll
                        sx={{
                          fontSize: 16,
                          color: "rgba(0,0,0,0.45)",
                          ml: 0.25,
                        }}
                      />
                    )}
                    {readStatus === "sent" && (
                      // ✓ gris tenue = ENVIADO
                      <Done
                        sx={{
                          fontSize: 16,
                          color: "rgba(0,0,0,0.25)",
                          ml: 0.25,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })}
          <div ref={bottomRef} />
        </Box>
      </Box>

      {/* Botón scroll to bottom */}
      {showScrollBtn && (
        <Fab
          size="small"
          onClick={scrollToBottom}
          sx={{
            position: "fixed",
            bottom: NAV_OFFSET + 80,
            right: 20,
            bgcolor: "rgba(0,212,255,0.2)",
            color: "#00d4ff",
            border: "1px solid rgba(0,212,255,0.4)",
            zIndex: 20,
            "&:hover": { bgcolor: "rgba(0,212,255,0.3)" },
          }}
        >
          <KeyboardArrowDown />
        </Fab>
      )}

      {/* Input fijo en el borde inferior */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0, right: 0,
          px: 2,
          py: 1,
          bgcolor: isLightTheme ? "rgba(255,255,255,0.94)" : "rgba(10,10,10,0.92)",
          backdropFilter: "blur(12px)",
          borderTop: `1px solid ${onSurface(0.07)}`,
          zIndex: 15,
          width: "100%",
          maxWidth: "100vw",
          boxSizing: "border-box",
          overflowX: "hidden",
        }}
      >
        {showEmoji && (
          <Box
            sx={{
              maxWidth: 680, mx: "auto", mb: 1,
              bgcolor: isLightTheme ? "rgba(255,255,255,0.98)" : "rgba(20,20,20,0.98)",
              border: `1px solid ${onSurface(0.1)}`,
              borderRadius: "14px",
              p: 1.5,
              display: "flex", flexWrap: "wrap", gap: 0.5,
            }}
          >
            {EMOJIS.map((e) => (
              <Box
                key={e}
                onClick={() => { setText((prev) => prev + e); }}
                sx={{ fontSize: 22, cursor: "pointer", lineHeight: 1, p: 0.3, borderRadius: "6px", "&:hover": { bgcolor: onSurface(0.1) } }}
              >
                {e}
              </Box>
            ))}
          </Box>
        )}

        <input ref={imageInputRef} type="file" accept="image/*,video/*" style={{ display: "none" }} />
        <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx" style={{ display: "none" }} />

        <Box
          sx={{
            maxWidth: 680, mx: "auto", width: "100%", boxSizing: "border-box",
            display: "flex", alignItems: "center",
            bgcolor: onSurface(0.07),
            border: `1px solid ${onSurface(0.1)}`,
            borderRadius: "20px",
            px: 1,
            py: 0.75,
            gap: 0.5,
            overflowX: "hidden",
          }}
        >
          <IconButton size="small" onClick={() => setShowEmoji((v) => !v)} sx={{ color: showEmoji ? "#00d4ff" : onSurface(0.5), "&:hover": { color: "#00d4ff" } }}>
            <EmojiEmotions fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => imageInputRef.current?.click()} sx={{ color: onSurface(0.5), "&:hover": { color: "#00d4ff" } }}>
            <ImageIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => docInputRef.current?.click()} sx={{ color: onSurface(0.5), "&:hover": { color: "#00d4ff" } }}>
            <AttachFile fontSize="small" />
          </IconButton>

          <Box
            component="textarea"
            placeholder={t("typeMessage")}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              const el = e.target;
              el.style.height = "24px";
              el.style.height = Math.min(el.scrollHeight, 80) + "px";
            }}
            onKeyDown={handleKeyDown}
            sx={{
              flex: 1,
              minWidth: 0,
              background: "none",
              border: "none",
              outline: "none",
              color: onSurface(1),
              fontSize: "0.9rem",
              lineHeight: "24px",
              height: "24px",
              minHeight: "24px",
              resize: "none",
              py: 0,
              px: 0.5,
              fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
              maxHeight: "80px",
              overflowY: "auto",
              verticalAlign: "middle",
              display: "block",
              "&::placeholder": { color: onSurface(0.35) },
            }}
          />

          <IconButton
            size="small"
            onClick={sendMessage}
            disabled={!text.trim() || sending}
            sx={{
              color: text.trim() ? "#00d4ff" : onSurface(0.3),
              bgcolor: text.trim() ? "rgba(0,212,255,0.15)" : "transparent",
              borderRadius: "50%",
              transition: "all 0.2s",
              "&:hover": { bgcolor: "rgba(0,212,255,0.25)" },
              mb: 0.25,
            }}
          >
            {sending ? <CircularProgress size={18} sx={{ color: "#00d4ff" }} /> : <Send fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Componente principal exportado ─────────────────────────────────────────
const Mensajes = ({ onBack, initialChat, allowNotifications = true, language = "es", themeMode = "oscuro" }) => {
  const t = useTranslatedDictionary(MESSAGES_TEXT, language);
  const [activeChat, setActiveChat] = useState(initialChat || null);
  const [notificationEnabled, setNotificationEnabled] = useState(
    allowNotifications && canUseNotifications() && Notification.permission === "granted"
  );
  const foregroundUnsubRef = useRef(null);
  const hasWarnedMissingVapidRef = useRef(false);

  const registerPushToken = async ({silent = true} = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser || !("serviceWorker" in navigator)) return;

    if (canUseNotifications() && Notification.permission === "default") {
      try {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;
      } catch (error) {
        return;
      }
    }

    if (canUseNotifications() && Notification.permission !== "granted") return;

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      if (!silent && !hasWarnedMissingVapidRef.current) {
        console.warn("REACT_APP_FIREBASE_VAPID_KEY no configurada.");
        hasWarnedMissingVapidRef.current = true;
      }
      return;
    }

    const supported = await isSupported();
    if (!supported) return;

    try {
      const swRegistration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      const messaging = getMessaging();
      const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: swRegistration });
      if (!token) return;

      await setDoc(
        doc(db, "users", currentUser.uid, "private", "data"),
        { fcmToken: token, fcmTokens: arrayUnion(token), fcmUpdatedAt: serverTimestamp() },
        { merge: true }
      );

      if (!foregroundUnsubRef.current) {
        foregroundUnsubRef.current = onMessage(messaging, (payload) => {
          notifyMessage({
            title: payload?.notification?.title || t("newMessageFallback"),
            body: payload?.notification?.body || t("newActivityFallback"),
          });
        });
      }
    } catch (error) {
      if (!silent) console.error("Error en flujo de registro de push:", error);
    }
  };

  const requestNotifications = async () => {
    if (!allowNotifications) { setNotificationEnabled(false); return; }
    if (!canUseNotifications()) return;
    if (Notification.permission === "granted") {
      setNotificationEnabled(true);
      await registerPushToken({silent: false}).catch(console.error);
      return;
    }
    if (Notification.permission === "denied") { setNotificationEnabled(false); return; }
    try {
      const permission = await Notification.requestPermission();
      setNotificationEnabled(permission === "granted");
      if (permission === "granted") await registerPushToken({silent: false});
    } catch {
      setNotificationEnabled(false);
    }
  };

  useEffect(() => {
    if (!allowNotifications) {
      setNotificationEnabled(false);
      if (foregroundUnsubRef.current) { foregroundUnsubRef.current(); foregroundUnsubRef.current = null; }
      return;
    }
    if (!notificationEnabled) return;
    registerPushToken({silent: true}).catch(console.error);
  }, [allowNotifications, notificationEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (foregroundUnsubRef.current) { foregroundUnsubRef.current(); foregroundUnsubRef.current = null; }
    };
  }, []);

  if (activeChat) {
    return (
      <ChatIndividual
        chatInfo={activeChat}
        onBack={() => setActiveChat(null)}
        onEnableNotifications={requestNotifications}
        notificationEnabled={notificationEnabled}
        t={t}
        themeMode={themeMode}
      />
    );
  }

  return (
    <ConversacionesList
      onBack={onBack}
      onOpenChat={(chatInfo) => setActiveChat(chatInfo)}
      onEnableNotifications={requestNotifications}
      notificationEnabled={notificationEnabled}
      t={t}
      themeMode={themeMode}
    />
  );
};

export default Mensajes;